import { useEffect } from "react";
import { Switch, Route, useLocation, useRouter } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { fetchMe } from "@/lib/auth";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { ThemeToggle } from "@/components/theme-toggle";
import Dashboard from "@/pages/dashboard";
import Products from "@/pages/products";
import Campaigns from "@/pages/campaigns";
import SettingsPage from "@/pages/settings";
import AnalysisPage from "@/pages/analysis";
import SchemaPage from "@/pages/schema";
import BusinessHub from "@/pages/business-hub";
import NotFound from "@/pages/not-found";
import ExecutiveSummary from "@/pages/executive-summary";
import ProDashboard from "@/pages/pro-dashboard";
import FinancialHub from "@/pages/financial-hub";
import MarketingIntelligence from "@/pages/marketing-intelligence";
import OperationsCentre from "@/pages/operations-centre";
import CustomerServiceHub from "@/pages/customer-service";
import ReportDistribution from "@/pages/report-distribution";
import VehicleUsage from "@/pages/vehicle-usage";
import WeeklyPhoneReport from "@/pages/weekly-phone-report";
import ChatPage from "@/pages/chat";
import ReportsPage from "@/pages/reports";
import Management from "@/pages/management";
import XeroFinance from "@/pages/xero-finance";
import LoginPage from "@/pages/login";
import ChangePasswordPage from "@/pages/change-password";
import AdminPage from "@/pages/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock } from "lucide-react";

// Maps SPA route → report registry slug for the access gate.
// Routes that don't appear here are considered always-allowed for logged-in users
// (e.g. /chat, /reports, /settings — internal tools, not reports).
const ROUTE_SLUGS: Record<string, string> = {
  "/": "management",
  "/management": "management",
  "/pro-dashboard": "pro-dashboard",
  "/executive-summary": "executive-summary",
  "/business-hub": "business-hub",
  "/financial-hub": "financial-hub",
  "/xero-finance": "xero-finance",
  "/marketing-intelligence": "marketing-intelligence",
  "/operations-centre": "operations-centre",
  "/customer-service": "customer-service",
  "/report-distribution": "report-distribution",
  "/vehicle-usage": "vehicle-usage",
  "/reports/weekly-phone": "weekly-phone-report",
};

function NoAccessPage({ slug }: { slug: string }) {
  return (
    <div className="p-6 flex items-center justify-center min-h-[60vh]">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <Lock className="h-8 w-8 text-muted-foreground" />
          <CardTitle>You don't have access to this report</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-center text-muted-foreground">
          Ask an administrator to grant you access to <code className="font-mono">{slug}</code>.
        </CardContent>
      </Card>
    </div>
  );
}

function GatedRoute({
  path,
  component: Component,
  permittedSlugs,
  isAdmin,
}: {
  path: string;
  component: React.ComponentType;
  permittedSlugs: string[];
  isAdmin: boolean;
}) {
  const slug = ROUTE_SLUGS[path];
  return (
    <Route path={path}>
      {() => {
        if (!slug) return <Component />;
        if (isAdmin || permittedSlugs.includes(slug)) return <Component />;
        return <NoAccessPage slug={slug} />;
      }}
    </Route>
  );
}

function GatedRouter({ permittedSlugs, isAdmin }: { permittedSlugs: string[]; isAdmin: boolean }) {
  return (
    <Switch>
      <GatedRoute path="/" component={Dashboard} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <Route path="/chat" component={ChatPage} />
      <Route path="/reports" component={ReportsPage} />
      <Route path="/products" component={Products} />
      <Route path="/products/:id" component={Products} />
      <Route path="/campaigns" component={Campaigns} />
      <Route path="/campaigns/:id" component={Campaigns} />
      <Route path="/settings" component={SettingsPage} />
      <Route path="/analysis" component={AnalysisPage} />
      <Route path="/schema" component={SchemaPage} />
      <GatedRoute path="/business-hub" component={BusinessHub} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/executive-summary" component={ExecutiveSummary} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/management" component={Management} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/pro-dashboard" component={ProDashboard} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/financial-hub" component={FinancialHub} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/xero-finance" component={XeroFinance} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/marketing-intelligence" component={MarketingIntelligence} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/operations-centre" component={OperationsCentre} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/customer-service" component={CustomerServiceHub} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/report-distribution" component={ReportDistribution} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/vehicle-usage" component={VehicleUsage} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <GatedRoute path="/reports/weekly-phone" component={WeeklyPhoneReport} permittedSlugs={permittedSlugs} isAdmin={isAdmin} />
      <Route path="/admin">{() => (isAdmin ? <AdminPage /> : <NoAccessPage slug="admin" />)}</Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function AuthedApp() {
  const [location] = useLocation();
  const { data: me, isLoading, isError } = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    retry: false,
  });

  // Unauthenticated routes.
  if (location === "/login") return <LoginPage />;

  useEffect(() => {
    if (!isLoading && !isError && me === null && location !== "/login") {
      window.location.href = "/login";
    }
  }, [isLoading, isError, me, location]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!me) {
    return (
      <div className="flex h-screen w-full items-center justify-center text-muted-foreground">
        Redirecting to sign-in…
      </div>
    );
  }

  // Forced password-change wall — everything else redirects here.
  if (me.user.mustChangePassword) {
    if (location !== "/change-password") {
      window.location.href = "/change-password";
      return (
        <div className="flex h-screen w-full items-center justify-center text-muted-foreground">
          Redirecting…
        </div>
      );
    }
    return <ChangePasswordPage />;
  }

  if (location === "/change-password") return <ChangePasswordPage />;

  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <TooltipProvider>
      <SidebarProvider style={style as React.CSSProperties}>
        <div className="flex h-screen w-full">
          <AppSidebar permittedSlugs={me.permittedSlugs} isAdmin={me.user.isAdmin} />
          <div className="flex flex-col flex-1 overflow-hidden">
            <header className="flex items-center justify-between gap-2 px-4 py-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <SidebarTrigger data-testid="button-sidebar-toggle" />
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">{me.user.email}</span>
                <ThemeToggle />
              </div>
            </header>
            <main className="flex-1 overflow-auto">
              <GatedRouter permittedSlugs={me.permittedSlugs} isAdmin={me.user.isAdmin} />
            </main>
          </div>
        </div>
      </SidebarProvider>
      <Toaster />
    </TooltipProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthedApp />
    </QueryClientProvider>
  );
}

export default App;
// Silences unused-import warning until/when we need useRouter directly.
export const _unusedUseRouter = useRouter;
